import React, { useRef, useState } from 'react';

import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { sendAIRequest } from 'components/admin/lib/helpers';
import { stopResponseGeneration } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/MyScribeTemplate/lib/helpers';
import AIChatInput from 'components/shared/AIChatInput/AIChatInput';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Icon from 'components/shared/Icon/Icon';

import useChatInput from '../../../practice/charts/ClinicalNote/MyScribeAI/Templates/MyScribeTemplate/hooks/useChatInput';

import {
  generalChatActivePromptConversationsState,
  generalChatIdState,
  generalChatLoading
} from './state';

export const ChatInput = () => {
  const { inputValue, disableGenerate, onTextChange } = useChatInput();
  const [isLoading, setIsLoading] = useRecoilState(generalChatLoading);
  const intelligenceChatId = useRecoilValue(generalChatIdState);
  const setActivePromptConversations = useSetRecoilState(generalChatActivePromptConversationsState);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null); // Add ref to control file input
  const MAX_TOTAL_SIZE = 50 * 1024 * 1000; // 50MB in bytes

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    // Calculate current total size of existing files
    const currentSize = files.reduce((sum, file) => sum + file.size, 0);

    // Calculate total size of new files
    const newFilesSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);

    // Check if adding new files would exceed total size limit
    if (currentSize + newFilesSize > MAX_TOTAL_SIZE) {
      showAlert({
        color: 'danger',
        message:
          'Unable to upload: The total file size exceeds the 50MB limit. Please select smaller files or reduce the number of files to stay within the limit.'
      });
      e.target.value = '';
      return;
    }

    // Append new files, ensuring no duplicates by name
    setFiles((prev) => {
      const existingNames = new Set(prev.map((file) => file.name));
      const newFiles = selectedFiles.filter((file) => !existingNames.has(file.name));
      return [...prev, ...newFiles];
    });

    e.target.value = '';
  };

  const convertFilesToBase64 = async () => {
    const attachments = [];

    for (const file of files) {
      try {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        attachments.push({
          name: file.name,
          type: file.type,
          data: base64Data,
          url: `data:${file.type};base64, ${base64Data}`
        });
      } catch (error) {
        console.error(`Failed to process ${file.name}: ${error.message}`);
        return [];
      }
    }

    return attachments;
  };

  const handleGenerate = () => {
    if (!inputValue?.length || (isLoading && intelligenceChatId)) {
      return;
    }
    onGenerate();
  };

  const onGenerate = async () => {
    const attachments = await convertFilesToBase64();

    setActivePromptConversations((prevPrompts = []) => [
      ...(prevPrompts || []),
      { id: self.crypto.randomUUID(), message: inputValue, agent: 'user', attachments }
    ]);

    setIsLoading(true);
    onTextChange({ target: { value: '' } });
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input
    }

    sendAIRequest({
      kind: 'myscribe-ai',
      params: {
        message: {
          userMessage: {
            message: inputValue,
            agent: 'user',
            attachments
          },
          body: inputValue,
          intelligenceChatId,
          kind: 'general_chat'
        }
      }
    });
  };

  const handleRemoveFile = (fileName) => {
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input after removing
    }
  };

  const onStopGeneration = () => {
    stopResponseGeneration({ intelligenceChatId });
    setIsLoading(false);
  };

  return (
    <div className="p-5">
      <AIChatInput
        isHTML
        className="mb-3 border-2 !border-neutral-200 shadow-none outline outline-1 outline-neutral-200"
        leftButtons={[]}
        rightButtons={[
          <div key="upload" className="flex items-center">
            <Tippy className="tippy-dark" content="Upload files" placement="top">
              <div>
                <Icon
                  icon="file-upload-mono"
                  size={24}
                  className="cursor-pointer"
                  htmlFor="file-upload"
                />
              </div>
            </Tippy>

            <input
              id="file-upload"
              type="file"
              multiple
              accept=".txt,.pdf,.png,.jpg,.jpeg,.csv,.docx,.rtf"
              className="hidden"
              onChange={handleFileChange}
              ref={fileInputRef} // Attach ref to input
            />
          </div>,
          <div className="flex items-center" key="generate-buttons">
            {isLoading && intelligenceChatId && (
              <Tippy content="Stop response" className="tippy-dark" placement="bottom">
                <div className="ml-2 mt-1 flex h-[32px] w-[32px] items-center justify-center">
                  <Icon
                    icon="stop-circle-filled"
                    size={32}
                    color="primary"
                    onClick={onStopGeneration}
                  />
                </div>
              </Tippy>
            )}
            {(!isLoading || !intelligenceChatId) && (
              <Button
                key="generate"
                icon="send-mono"
                iconSize={18}
                iconColor="white"
                className={cs('ml-2 mt-1 h-[32px] w-[32px] border-none')}
                onClick={handleGenerate}
                disabled={disableGenerate}
                type="secondary"
              />
            )}
          </div>
        ]}
        onTextChange={onTextChange}
        onSubmit={handleGenerate}
        placeholder="Ask MyScribe AI"
        value={inputValue}
        inputClassName="max-h-[192px] min-h-[32px] my-auto pt-1"
        inputContainerClassName="flex items-center">
        {files.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {files.map((file) => (
              <div
                key={file.name}
                className="flex items-center gap-2 rounded bg-neutral-100 px-2 py-1">
                <span className="text-sm text-neutral-700">{file.name}</span>
                <Icon icon="close-thick-linear" onClick={() => handleRemoveFile(file.name)} />
              </div>
            ))}
          </div>
        )}
      </AIChatInput>

      <p className="text-center text-xs italic text-neutral-700">
        AI may make mistakes. Always verify the information before using it.
      </p>
    </div>
  );
};
